import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link2, Unlink, UserPlus } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { BuscaRemota, Rotulo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post } from '../../nucleo/api';
import { documentoFormatado, nomePessoa } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Ficha } from './tipos';

interface ParceiroBusca {
  id: string;
  nome: string;
  documento: string | null;
}

/**
 * Fase 2 (W23, K10; decisão 14 do plano da fase 2): o funcionário não vira parceiro, ganha só uma ligação opcional com o
 * Cadastros (para o PJ ou para reembolso). Ligar a um parceiro que já existe (sugestão pelo CPF e a busca do Cadastros) ou
 * "Criar também como fornecedor" (com `cadastros.criar`; o CPF que já é de um parceiro só liga, sem duplicar). Nome e
 * documento do parceiro pelos formatadores do modo discreto. Só com o Cadastros instalado e o cadastro visível pela empresa.
 */
export function ParceiroCard({ ficha, podeEditar }: { ficha: Ficha; podeEditar: boolean }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [ligando, setLigando] = useState(false);
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [desligando, setDesligando] = useState(false);
  const verCadastros = s.pode('cadastros.ver');
  // Sugestão pelo CPF (a busca do Cadastros casa o documento): só ao abrir o diálogo de ligar.
  const mesmoCpf = useQuery({
    queryKey: ['pessoas', 'parceiro-cpf', ficha.id, ficha.cpf],
    queryFn: () => get<{ itens: ParceiroBusca[] }>('/cadastros/parceiros', { busca: ficha.cpf, limite: 5 }),
    enabled: ligando && verCadastros && !!ficha.cpf,
  });
  const sugestao = mesmoCpf.data?.itens.find((p) => (p.documento ?? '').replace(/\D/g, '') === (ficha.cpf ?? '').replace(/\D/g, ''));
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['pessoas'] });
    await qc.invalidateQueries({ queryKey: ['chatter'] });
  };
  const ligar = useMutation({
    mutationFn: (parceiroId: string | null) => api('PUT', `/pessoas/funcionarios/${ficha.id}/parceiro`, { parceiro_id: parceiroId }),
    onSuccess: async (_r, parceiroId) => {
      setLigando(false);
      setDesligando(false);
      setEscolhido(null);
      await recarregar();
      toast.sucesso(parceiroId ? t('parceiro.ligado') : tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const criar = useMutation({
    mutationFn: () => post<{ parceiro_id: string; criado: boolean }>(`/pessoas/funcionarios/${ficha.id}/parceiro`, {}),
    onSuccess: async (r) => {
      setCriando(false);
      await recarregar();
      toast.sucesso(r.criado ? t('parceiro.criado') : t('parceiro.jaExistia'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  if (!s.instalado('cadastros') || ficha.so_por_vinculo) return null;
  const p = ficha.parceiro;
  const podeLigar = podeEditar && verCadastros;
  const podeCriar = podeEditar && s.pode('cadastros.criar') && !!ficha.cpf;

  return (
    <Cartao
      titulo={t('parceiro.titulo')}
      acao={
        podeEditar &&
        ficha.parceiro_id && (
          <Botao variante="fantasma" tamanho="sm" icone={<Unlink className="size-4" />} onClick={() => setDesligando(true)}>
            {t('parceiro.desligar')}
          </Botao>
        )
      }
    >
      <div data-parceiro-funcionario className="flex flex-col gap-2 text-sm">
        <p className="text-xs text-tinta-3">{t('parceiro.ajuda')}</p>
        {ficha.parceiro_id ? (
          p ? (
            <p className="flex min-w-0 flex-wrap items-center gap-2">
              <Link to={`/cadastros/cadastros/parceiro/${p.id}`} title={t('parceiro.abrir')} className="min-w-0 font-medium break-words hover:text-acento-forte">
                {nomePessoa(p.nome)}
              </Link>
              {p.documento && <span className="numero text-tinta-3">{documentoFormatado(p.documento)}</span>}
              {p.arquivado && <Selo cor="perigo">{t('parceiro.arquivado')}</Selo>}
            </p>
          ) : (
            <p className="text-tinta-2">{t('parceiro.semAcesso')}</p>
          )
        ) : (
          <>
            <p className="text-tinta-3">{t('parceiro.vazio')}</p>
            {(podeLigar || podeCriar) && (
              <div className="flex flex-wrap gap-2">
                {podeLigar && (
                  <Botao variante="secundario" tamanho="sm" icone={<Link2 className="size-4" />} onClick={() => setLigando(true)}>
                    {t('parceiro.ligar')}
                  </Botao>
                )}
                {podeCriar && (
                  <Botao variante="secundario" tamanho="sm" icone={<UserPlus className="size-4" />} onClick={() => setCriando(true)}>
                    {t('parceiro.criar')}
                  </Botao>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        aberto={ligando}
        aoFechar={() => setLigando(false)}
        titulo={t('parceiro.ligarTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setLigando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!escolhido} carregando={ligar.isPending} onClick={() => ligar.mutate(escolhido)}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {sugestao && (
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-borda px-3 py-2 text-sm" data-parceiro-mesmo-cpf>
              <span className="min-w-0 break-words">
                <span className="text-tinta-3">{t('parceiro.mesmoCpf')}</span> <span className="font-medium">{nomePessoa(sugestao.nome)}</span>
              </span>
              <Botao variante="secundario" tamanho="sm" carregando={ligar.isPending} onClick={() => ligar.mutate(sugestao.id)}>
                {t('parceiro.ligarEste')}
              </Botao>
            </div>
          )}
          <Rotulo id="parceiro-funcionario" rotulo={t('parceiro.buscar')}>
            <BuscaRemota id="parceiro-funcionario" def={{ caminho: '/cadastros/parceiros', params: { operacao: true }, rotulo: (i) => String(i.nome) }} valor={escolhido} aoMudar={(v) => setEscolhido(v)} />
          </Rotulo>
        </div>
      </Modal>
      <Confirmar aberto={criando} aoFechar={() => setCriando(false)} aoConfirmar={() => criar.mutate()} titulo={t('parceiro.criar')} texto={t('parceiro.criarTexto')} carregando={criar.isPending} />
      <Confirmar aberto={desligando} aoFechar={() => setDesligando(false)} aoConfirmar={() => ligar.mutate(null)} titulo={t('parceiro.desligar')} texto={t('parceiro.desligarTexto')} carregando={ligar.isPending} />
    </Cartao>
  );
}
